import { fijarIdiomaCompartido, type IdiomaUI } from '@eg/compartido'
import { useQueryClient } from '@tanstack/react-query'
import { Languages } from 'lucide-react'
import { createContext, Fragment, useCallback, useContext, useState, type ReactNode } from 'react'
import { esDemo } from '../demo/modo'
import { en } from './en'
import { es } from './es'

export type Idioma = IdiomaUI

/** Solo la demostración es bilingüe y abre en inglés; la tienda real de México queda en español. */
export const hayIdiomas = esDemo
const IDIOMA_INICIAL: Idioma = esDemo ? 'en' : 'es'
const CLAVE = 'rm-idioma-v1'

type Arbol = Record<string, unknown>

/** Copia profunda que conserva las funciones (structuredClone no las admite). */
function clonar<T>(v: T): T {
  if (Array.isArray(v)) return v.map(clonar) as T
  if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, clonar(x)])) as T
  return v
}

/**
 * Vuelca `fuente` sobre `destino` sin reemplazar objetos ni listas: los módulos que guardaron una rama
 * (`const t = es.inicio`) siguen apuntando al mismo objeto y ven el idioma nuevo.
 */
function volcar(destino: Arbol, fuente: Arbol) {
  for (const [k, valor] of Object.entries(fuente)) {
    const actual = destino[k]
    if (valor && typeof valor === 'object' && actual && typeof actual === 'object') volcar(actual as Arbol, valor as Arbol)
    else destino[k] = valor
  }
}

const ESPANOL = clonar(es)
let actual: Idioma = 'es'

export function idiomaActual(): Idioma {
  return actual
}

function leerGuardado(): Idioma | null {
  try {
    const v = window.localStorage.getItem(CLAVE)
    return v === 'es' || v === 'en' ? v : null
  } catch {
    return null
  }
}

/** Idioma al abrir: el de la URL (?lang=es), el que la persona eligió antes o el de la compilación. */
function idiomaAlAbrir(): Idioma {
  if (!hayIdiomas) return 'es'
  const deUrl = new URLSearchParams(window.location.search).get('lang')
  if (deUrl === 'es' || deUrl === 'en') return deUrl
  return leerGuardado() ?? IDIOMA_INICIAL
}

/** Cambia todos los textos de la tienda y de los componentes compartidos, el lang del documento y su título. */
export function aplicarIdioma(idioma: Idioma, recordar = false) {
  actual = idioma
  volcar(es as unknown as Arbol, (idioma === 'en' ? en : ESPANOL) as unknown as Arbol)
  fijarIdiomaCompartido(idioma)
  document.documentElement.lang = idioma === 'en' ? 'en' : 'es'
  document.title = es.tituloDocumento
  if (!recordar) return
  try {
    window.localStorage.setItem(CLAVE, idioma)
  } catch {
    /* sin almacenamiento: el idioma dura mientras la pestaña esté abierta */
  }
}

/** Se llama una vez antes de dibujar la tienda. */
export function iniciarIdioma() {
  aplicarIdioma(idiomaAlAbrir())
}

const Contexto = createContext<{ idioma: Idioma; cambiar: (i: Idioma) => void }>({ idioma: 'es', cambiar: () => {} })

/**
 * Al cambiar de idioma se vuelven a montar las pantallas (key) y se descartan las consultas en caché, para que
 * los textos y el catálogo de la demostración lleguen en el idioma nuevo.
 */
export function ProveedorIdioma({ children }: { children: ReactNode }) {
  const [idioma, setIdioma] = useState<Idioma>(idiomaActual)
  const consultas = useQueryClient()
  const cambiar = useCallback(
    (i: Idioma) => {
      aplicarIdioma(i, true)
      setIdioma(i)
      void consultas.resetQueries()
    },
    [consultas],
  )
  return (
    <Contexto.Provider value={{ idioma, cambiar }}>
      <Fragment key={idioma}>{children}</Fragment>
    </Contexto.Provider>
  )
}

/** Botón de la cabecera para pasar al otro idioma. Muestra el nombre del idioma de destino en ese idioma. */
export function BotonIdioma() {
  const { idioma, cambiar } = useContext(Contexto)
  if (!hayIdiomas) return null
  const otro: Idioma = idioma === 'en' ? 'es' : 'en'
  return (
    <button type="button" className="boton boton--secundario boton--compacto boton-idioma" onClick={() => cambiar(otro)} title={es.idioma.cambiar}>
      <Languages className="icono" aria-hidden="true" />
      <span lang={otro}>
        <span className="boton-idioma__largo">{es.idioma.nombreOtro}</span>
        <span className="boton-idioma__corto" aria-hidden="true">
          {otro.toUpperCase()}
        </span>
      </span>
    </button>
  )
}
